import type { ChainKey, MainnetChainKey } from '@infinex/chain-config';
import { CHAIN_KEYS } from '@infinex/chain-config';

import type { AssetId, AssetType } from './assets';
import { isValidChainAddress, makeChainAddressId } from './assets';
import type { StableAssetCategory } from './categories';
import type { ChainAddressId, ChainAddressIdObject } from './chainAssets';
import type { MarketDataProviderType } from './marketDataProvider';
import type { SocialLinks } from './socials';

export type OfficialAssetGroupId = { assetId: AssetId };
type ExtendedAssetGroupId = ChainAddressIdObject;
export type AssetGroupId = OfficialAssetGroupId | ExtendedAssetGroupId;

/**
 * CAUTION: Only use this when absolutely necessary, as formulating the groupId
 * from this string is non-deterministic and should be avoided
 *
 * A string representation of an asset group id
 */
export type AssetGroupIdString = AssetId | ChainAddressId;

/**
 * Represents an Asset on a particular chain,
 * which may or may not belong to a group
 */
export type ChainAsset = {
  /** this is the chain_address_id */
  id: ChainAddressId;
  assetId?: AssetId;
  /** @deprecated Use `assetId` instead, this is getting removed soon */
  assetGroupId?: AssetId;
  chain: ChainKey;
  decimals: number;
  address: string;
  name: string;
  symbol: string;
  imgUrl: string;
  type: AssetType;
  coingeckoId: string | null;
  socials?: SocialLinks;
  liquidity?: number;
  primaryChain?: ChainKey;
  stableRootAsset?: StableAssetCategory;
  isAlwaysFetched?: boolean;
  isManualControlled?: boolean;
  createdAt?: number;
  /**
   * This member's `chain_asset_flags.is_official`. Officialness describes the
   * asset, so every writer keeps it identical across members sharing an
   * `assetId` — this is the group's answer too.
   */
  isOfficial: boolean;
  /** Operator override; null means routing derives from officialness. */
  preferredMarketDataProvider: MarketDataProviderType | null;
};

export type MainnetChainAsset = Omit<ChainAsset, 'chain'> & {
  chain: MainnetChainKey;
};

type BaseGroupedAsset = {
  type: AssetType;
  name: string;
  symbol: string;
  imgUrl: string;
  members: Partial<Record<ChainKey, ChainAsset>>;
};

export type GroupedAsset = BaseGroupedAsset & {
  groupId: AssetGroupId;
};

export type OfficialGroupedAsset = BaseGroupedAsset & {
  groupId: OfficialAssetGroupId;
};

export type ExtendedGroupedAsset = BaseGroupedAsset & {
  groupId: ChainAddressIdObject;
};

export type OfficialGroupedAssetsByAssetId = Partial<
  Record<AssetId, OfficialGroupedAsset>
>;

/**
 * Convert a AssetGroupIdString to a AssetGroupId
 *
 * NOTE: this is not determistic and presumes the id passed in is valid
 *
 * @param groupId - The AssetGroupIdString to convert
 * @returns The converted AssetGroupId
 */
export const getGroupIdFromString = (
  groupId: AssetGroupIdString
): AssetGroupId => {
  const all = groupId.split('_');
  const potentialChain = all[0] as ChainKey;
  const potentialAddress = all[1];

  if (
    CHAIN_KEYS.includes(potentialChain) &&
    isValidChainAddress(potentialAddress) &&
    all.length === 2
  ) {
    return {
      chain: potentialChain,
      address: potentialAddress,
    };
  }

  return {
    assetId: groupId,
  };
};

/**
 * Convert an AssetGroupId to an AssetGroupIdString
 *
 * @param groupId - The AssetGroupId to convert
 * @returns The converted AssetGroupIdString
 */
export const getGroupIdStringFromId = (
  groupId: AssetGroupId
): AssetGroupIdString => {
  if ('assetId' in groupId) {
    return groupId.assetId;
  }

  return makeChainAddressId(groupId.chain, groupId.address);
};

/**
 * Make an AssetGroupId from something resembling a chain asset/balance
 *
 * @param params - The params to make the groupId from
 * @returns The AssetGroupId
 */
export const makeGroupId = <
  T extends { chain: ChainKey; address: string; assetGroupId?: AssetId },
>(
  params: T
): AssetGroupId => {
  if (params.assetGroupId) {
    return { assetId: params.assetGroupId };
  }
  return { chain: params.chain, address: params.address };
};

/**
 * Narrow a wider object that satisfies `AssetGroupId` down to just the
 * `AssetGroupId` fields, discarding any extras. Use when a component holds
 * rendering props alongside identifier fields and a downstream API
 * (e.g. tRPC `assets.getAssetMetadata` with its `z.strictObject` lookup
 * schema) rejects unknown keys.
 */
export const extractAssetGroupId = <T extends AssetGroupId>(
  props: T
): AssetGroupId => {
  if ('assetId' in props && props.assetId != null) {
    return { assetId: props.assetId };
  }
  // Unofficial assets can arrive with the `assetId` key present but
  // undefined; without a defined `assetId` the union is only satisfiable as
  // the chain/address variant.
  const { chain, address } = props as ChainAddressIdObject;
  return { chain, address };
};

/**
 * Ensure deprecated `assetGroupId` mirrors `assetId`, and `id` is set from
 * chain + address.
 */
export const ensureChainAssetId = (
  chainAsset: ChainAsset & { assetId: AssetId }
): ChainAsset & { assetId: AssetId } => {
  return {
    ...chainAsset,
    id: makeChainAddressId(chainAsset.chain, chainAsset.address),
    assetGroupId: chainAsset.assetId,
  };
};

export const groupOfficialChainAssets = (
  officialChainAssets: (ChainAsset & { assetId: AssetId })[]
): OfficialGroupedAssetsByAssetId => {
  return officialChainAssets.reduce((acc, chainAsset) => {
    const member = ensureChainAssetId(chainAsset);
    const existingGroup = acc[chainAsset.assetId];

    if (existingGroup) {
      existingGroup.members[chainAsset.chain] = member;
    } else {
      acc[chainAsset.assetId] = {
        groupId: { assetId: chainAsset.assetId },
        type: chainAsset.type,
        name: chainAsset.name,
        symbol: chainAsset.symbol,
        imgUrl: chainAsset.imgUrl,
        members: {
          [chainAsset.chain]: member,
        },
      };
    }
    return acc;
  }, {} as OfficialGroupedAssetsByAssetId);
};

export const areGroupIdsEqual = (
  groupId1: AssetGroupId,
  groupId2: AssetGroupId
): boolean => {
  if ('assetId' in groupId1 && 'assetId' in groupId2) {
    return groupId1.assetId === groupId2.assetId;
  }

  if ('chain' in groupId1 && 'chain' in groupId2) {
    return (
      groupId1.chain === groupId2.chain && groupId1.address === groupId2.address
    );
  }

  return false;
};
